import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearchComponent from './SearchMC';
import IconMC from './IconMC';
import TagMC from './TagMC';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * AsyncPaginatedMultiSelectMC component
 * A multi-select dropdown component with async pagination support, styled like MultiSelectDropdownMC
 *
 * @param {string} label - Text label to display above the dropdown.
 * @param {Function} loadOptions - Async function to load options (receives inputValue, loadedOptions, { page })
 * @param {Function} onSelect - Function called when options are selected. Receives (name, selectedValues[])
 * @param {Array} [defaultValue] - Initial selected values array.
 * @param {string} [placeholder] - Placeholder text when no option is selected.
 * @param {boolean} [search] - If true, a search input will be displayed (default: true).
 * @param {string} [className] - Additional classes to apply to the dropdown.
 * @param {string} [name] - Name for the field (used in onSelect callback).
 * @param {number} [maxSelected] - Maximum number of selectable items.
 */
/**
 * Parse default value to ensure it's an array of values
 */
const parseDefaultValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? parseInt(v, 10) || v : v));
  }
  if (typeof value === 'string') {
    return value.split(',').map((v) => parseInt(v.trim(), 10) || v.trim()).filter(Boolean);
  }
  return [value];
};

const AsyncPaginatedMultiSelectMC = ({
  label,
  loadOptions,
  onSelect,
  defaultValue = [],
  placeholder,
  search = true,
  className = '',
  name,
  maxSelected,
  searchDebounceTime = 500,
  useFloatingLabel = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const parsedDefaultValue = parseDefaultValue(defaultValue);
  const [selectedValues, setSelectedValues] = useState(parsedDefaultValue);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [minCharsRequired, setMinCharsRequired] = useState(false);
  const listRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const domNode = useClickOutside(() => {
    setIsOpen(false);
  });

  // Debounce search term
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    if (value !== debouncedSearchTerm) {
      setIsSearching(true);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchTerm(value);
        setIsSearching(false);
      }, searchDebounceTime);
    }
  }, [debouncedSearchTerm, searchDebounceTime]);

  // Cleanup debounce timer on unmount
  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  // Load initial options when dropdown opens
  const loadInitialOptions = useCallback(async () => {
    setIsLoading(true);
    setMinCharsRequired(false);
    try {
      const result = await loadOptions(debouncedSearchTerm, [], { page: 1 });
      if (result.minCharsRequired) {
        setMinCharsRequired(true);
        setOptions([]);
        setHasMore(false);
      } else {
        setOptions(result.options || []);
        setHasMore(result.hasMore || false);
        setPage(2);
      }
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadOptions, debouncedSearchTerm]);

  // Load more options when scrolling
  const loadMoreOptions = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const result = await loadOptions(debouncedSearchTerm, options, { page });
      setOptions((prev) => [...prev, ...(result.options || [])]);
      setHasMore(result.hasMore || false);
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error('Error loading more options:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [loadOptions, debouncedSearchTerm, options, page, isLoadingMore, hasMore]);

  // Reset and reload when debounced search term changes
  useEffect(() => {
    if (isOpen) {
      setOptions([]);
      setPage(1);
      setHasMore(true);
      loadInitialOptions();
    }
  }, [debouncedSearchTerm, isOpen]);

  // Load initial selected options labels by searching through pages
  useEffect(() => {
    const loadSelectedOptionsLabels = async () => {
      if (initialLoadDone || parsedDefaultValue.length === 0) return;

      setInitialLoadDone(true);
      const allFoundOptions = [];
      const valuesToFind = [...parsedDefaultValue];
      let currentPage = 1;
      let hasMorePages = true;

      // Load pages until we find all selected options or run out of pages
      while (valuesToFind.length > 0 && hasMorePages && currentPage <= 10) {
        try {
          const result = await loadOptions('', [], { page: currentPage });
          const pageOptions = result.options || [];

          // Find matching options in this page
          pageOptions.forEach((opt) => {
            const matchIndex = valuesToFind.findIndex(
              (val) => String(opt.value) === String(val)
            );
            if (matchIndex !== -1) {
              allFoundOptions.push(opt);
              valuesToFind.splice(matchIndex, 1);
            }
          });

          hasMorePages = result.hasMore || false;
          currentPage += 1;
        } catch (error) {
          console.error('Error loading options for default values:', error);
          break;
        }
      }

      if (allFoundOptions.length > 0) {
        setSelectedOptions(allFoundOptions);
        setSelectedValues(allFoundOptions.map((opt) => opt.value));
      }
    };

    loadSelectedOptionsLabels();
  }, [parsedDefaultValue.length]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreOptions();
    }
  }, [loadMoreOptions]);

  const handleSelect = (option) => {
    let newSelectedValues;
    let newSelectedOptions;

    if (selectedValues.includes(option.value)) {
      // Deselect if already selected
      newSelectedValues = selectedValues.filter((val) => val !== option.value);
      newSelectedOptions = selectedOptions.filter((opt) => opt.value !== option.value);
    } else {
      // Check if max selected reached
      if (maxSelected && selectedValues.length >= maxSelected) {
        showtoast(t('dropdown_mc.max_selected_reached', { max: maxSelected }));
        return;
      }
      newSelectedValues = [...selectedValues, option.value];
      newSelectedOptions = [...selectedOptions, option];
    }

    setSelectedValues(newSelectedValues);
    setSelectedOptions(newSelectedOptions);

    if (onSelect) {
      onSelect(name, newSelectedValues);
    }
  };

  const removeSelected = (valueToRemove, e) => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.filter((val) => val !== valueToRemove);
    const newSelectedOptions = selectedOptions.filter((opt) => opt.value !== valueToRemove);

    setSelectedValues(newSelectedValues);
    setSelectedOptions(newSelectedOptions);

    if (onSelect) {
      onSelect(name, newSelectedValues);
    }
  };

  // Filter out already selected options from the list
  const availableOptions = options.filter(
    (opt) => !selectedValues.includes(opt.value) || opt.value === ''
  );

  const buttonClassName = [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-px-4',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    'tw-min-h-[46px]',
    useFloatingLabel ? 'tw-pt-5 tw-pb-1' : 'tw-py-2.5'
  ].join(' ');

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-text-xs',
    'tw-text-blue-500',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-start-4'
  ].join(' ');

  const dropdownClass = [
    'tw-absolute',
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-z-50',
    'tw-w-full',
    'tw-mt-1'
  ].join(' ');

  const listItemClassName = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' ');

  return (
    <div className={`tw-wrapper tw-relative tw-w-full ${className}`} ref={domNode}>
      {!useFloatingLabel && label && (
        <label className="tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName}
      >
        <div className="tw-w-full">
          {useFloatingLabel && label && (
            <label className={floatingLabelClassNames}>
              {label}
            </label>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-overflow-hidden tw-text-gray-700">
            {selectedOptions.length === 0 ? (
              <span className="tw-text-gray-400 tw-text-default/[26px]">
                {placeholder || t('customer.view_list.modal_filters.all')}
              </span>
            ) : (
              selectedOptions.map((option) => (
                <TagMC
                  key={option.value}
                  label={option.label}
                  onClick={(e) => removeSelected(option.value, e)}
                />
              ))
            )}
          </div>

          <div className="tw-absolute tw-top-1/2 tw-right-4 tw-transform -tw-translate-y-1/2">
            <IconMC
              name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
              size="base"
              className="tw-text-gray-600"
            />
          </div>
        </div>
      </button>

      {isOpen && (
        <div className={dropdownClass}>
          {search && (
            <div className="tw-px-2 tw-pt-2">
              <SearchComponent
                placeholder="Buscar..."
                onChange={handleSearchChange}
                inputClassName="tw-text-sm tw-bg-white"
                debounceTime={0}
              />
            </div>
          )}

          <ul
            ref={listRef}
            onScroll={handleScroll}
            className="tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0"
          >
            {(isSearching || isLoading) && availableOptions.length === 0 ? (
              <li className="tw-px-4 tw-py-6 tw-text-gray-500 tw-text-center tw-flex tw-flex-col tw-items-center tw-gap-2">
                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-300 tw-border-t-blue-500" />
                <span className="tw-text-sm">
                  {isSearching ? t('dropdown_mc.waiting_search') : t('dropdown_mc.loading')}
                </span>
              </li>
            ) : minCharsRequired ? (
              <li className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-center">
                {t('dropdown_mc.min_chars_required', { defaultValue: 'Escribe al menos 3 caracteres para buscar' })}
              </li>
            ) : availableOptions.length === 0 && !isLoadingMore ? (
              <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                {t('dropdown_mc.no_options')}
              </li>
            ) : (
              <>
                {availableOptions.map((option) => (
                  <li
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    className={`${listItemClassName} ${
                      selectedValues.includes(option.value)
                        ? 'tw-bg-blue-500 tw-text-white'
                        : ''
                    }`}
                  >
                    <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-w-4/5">
                      {option.label}
                    </span>
                  </li>
                ))}
                {isLoadingMore && (
                  <li className="tw-px-4 tw-py-3 tw-text-gray-500 tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-500" />
                    <span className="tw-text-sm">{t('dropdown_mc.loading')}</span>
                  </li>
                )}
              </>
            )}
          </ul>

          {maxSelected && (
            <div className="tw-px-4 tw-py-2 tw-text-xs tw-text-gray-500 tw-border-t">
              {t('dropdown_mc.selected_count', {
                count: selectedValues.length,
                max: maxSelected
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AsyncPaginatedMultiSelectMC;

